<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAiAnalytics } from 'dashboard/composables/useAiAnalytics';
import { AI_INBOX_MAP, PERIOD_OPTIONS } from 'dashboard/constants/aiAgents';
import conversationApi from 'dashboard/api/conversations';
import AuditStats from './AuditStats.vue';

const activeTab = ref('overview'); // 'overview' | 'audit'

const route  = useRoute();
const router = useRouter();

const {
  isLoading,
  error,
  fetchOverview,
  conversations,
  aiOnly,
  transferred,
  avgDurationFormatted,
  avgTimeToTransferFormatted,
  byUnit,
  topIntents,
  delta,
} = useAiAnalytics();

const selectedPeriod  = ref('today');
const selectedInboxId = ref(null);
const customFrom = ref('');
const customTo   = ref('');
const inboxDropdownOpen = ref(false);
const inboxDropdownRef  = ref(null);
const customPickerOpen = ref(false);
const customPickerRef  = ref(null);

const inboxOptions = [
  { label: 'Todas as unidades', value: null },
  ...Object.entries(AI_INBOX_MAP).map(([id, { name }]) => ({
    label: name,
    value: Number(id),
  })),
];

const selectedInboxLabel = computed(
  () => inboxOptions.find(o => o.value === selectedInboxId.value)?.label ?? 'Todas as unidades'
);

const selectInbox = value => {
  selectedInboxId.value = value;
  inboxDropdownOpen.value = false;
};

const handleOutsideClick = e => {
  if (inboxDropdownRef.value && !inboxDropdownRef.value.contains(e.target)) {
    inboxDropdownOpen.value = false;
  }
  if (customPickerRef.value && !customPickerRef.value.contains(e.target)) {
    customPickerOpen.value = false;
  }
};

watch(inboxDropdownOpen, isOpen => {
  if (isOpen) document.addEventListener('click', handleOutsideClick);
  else document.removeEventListener('click', handleOutsideClick);
});

watch(customPickerOpen, isOpen => {
  if (isOpen) document.addEventListener('click', handleOutsideClick);
  else document.removeEventListener('click', handleOutsideClick);
});

onUnmounted(() => document.removeEventListener('click', handleOutsideClick));

const customLabel = computed(() => {
  if (selectedPeriod.value !== 'custom' || (!customFrom.value && !customTo.value)) {
    return 'Personalizado';
  }
  const fmt = d => {
    if (!d) return '—';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y.slice(2)}`;
  };
  return `${fmt(customFrom.value)} – ${fmt(customTo.value)}`;
});

const handleCustomClick = () => {
  if (selectedPeriod.value !== 'custom') {
    selectedPeriod.value = 'custom';
    customPickerOpen.value = true;
  } else {
    customPickerOpen.value = !customPickerOpen.value;
  }
};

const applyCustomPeriod = () => {
  customPickerOpen.value = false;
  loadData();
};

// Modal de conversas (reutilizado para motivos e intents)
const activeModal       = ref(null); // { title, conversation_ids }
const modalConversations = ref([]);  // [{ id, name }]

const openConvModal = async ({ title, conversation_ids }) => {
  activeModal.value = { title, conversation_ids };
  modalConversations.value = conversation_ids.map(id => ({ id, name: null }));

  await Promise.all(
    conversation_ids.map(async (convId, idx) => {
      try {
        const { data } = await conversationApi.show(convId);
        modalConversations.value[idx] = {
          id: convId,
          name: data.meta?.sender?.name || null,
        };
      } catch {
        // mantém name: null, exibe fallback no template
      }
    })
  );
};

const closeConvModal = () => {
  activeModal.value = null;
  modalConversations.value = [];
};

const openMotivoModal = motivo =>
  openConvModal({ title: motivo.motivo, conversation_ids: motivo.conversation_ids });

const openIntentModal = item =>
  openConvModal({ title: item.intent, conversation_ids: item.conversation_ids ?? [] });

const goToConversation = convId => {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: convId },
  });
  closeConvModal();
};

const periodTimes = computed(() => {
  const now = Math.floor(Date.now() / 1000);
  const opt = PERIOD_OPTIONS.find(p => p.key === selectedPeriod.value);

  if (opt?.key === 'today') {
    const d = new Date();
    d.setUTCHours(3, 0, 0, 0);
    if (d.getTime() > Date.now()) d.setUTCDate(d.getUTCDate() - 1);
    return { since: Math.floor(d.getTime() / 1000), until: now };
  }

  if (opt?.key === 'custom') {
    // BRT = UTC-3 → início do dia = 03:00 UTC; fim do dia = +1 dia 02:59:59 UTC
    const from = customFrom.value || new Date(now * 1000 - 30 * 86400000).toISOString().slice(0, 10);
    const to   = customTo.value   || new Date(now * 1000).toISOString().slice(0, 10);
    const since = Math.floor(new Date(from).getTime() / 1000) + 3 * 3600;
    const until = Math.floor(new Date(to).getTime() / 1000)   + 27 * 3600 - 1;
    return { since, until };
  }

  return { since: now - (opt?.days ?? 0) * 86400, until: now };
});

const loadData = () => {
  const { since, until } = periodTimes.value;
  fetchOverview({ accountId: route.params.accountId, since, until, inboxId: selectedInboxId.value });
};

watch([selectedPeriod, selectedInboxId], loadData, { immediate: true });

const formatDelta = pct => {
  if (pct === null || pct === undefined) return null;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct}%`;
};

const deltaClass = pct => {
  if (!pct) return 'text-slate-400';
  return pct > 0 ? 'text-emerald-500' : 'text-red-400';
};

const formatTime = secs => {
  if (!secs || secs === 0) return null;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6 overflow-y-auto">
    <!-- Header -->
    <div class="flex items-start justify-between flex-wrap gap-4">
      <div class="flex items-center gap-3">
        <div
          class="w-9 h-9 rounded-xl bg-woot-500/10 dark:bg-woot-500/15 flex items-center justify-center shrink-0"
        >
          <span class="i-lucide-brain-circuit text-woot-500 text-lg" />
        </div>
        <div>
          <h1 class="text-base font-semibold text-slate-800 dark:text-slate-100 leading-snug">
            Reports IA
          </h1>
          <p class="text-xs text-slate-400 dark:text-slate-500 leading-snug">
            Performance dos agentes de IA
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2 flex-wrap">
        <div ref="inboxDropdownRef" class="relative">
          <button
            class="flex items-center gap-1.5 text-xs font-medium border border-slate-200 dark:border-slate-700 rounded-full pl-3 pr-2.5 py-1.5 bg-white dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-800 dark:hover:text-slate-100 transition-colors cursor-pointer"
            @click.stop="inboxDropdownOpen = !inboxDropdownOpen"
          >
            <span>{{ selectedInboxLabel }}</span>
            <span
              class="i-lucide-chevron-down text-slate-400 text-xs transition-transform duration-200"
              :class="{ 'rotate-180': inboxDropdownOpen }"
            />
          </button>

          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-1"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 -translate-y-1"
          >
            <div
              v-if="inboxDropdownOpen"
              class="absolute right-0 top-full mt-1.5 z-50 min-w-[168px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden"
            >
              <button
                v-for="opt in inboxOptions"
                :key="String(opt.value)"
                class="w-full flex items-center justify-between gap-3 text-left text-xs px-3 py-2 transition-colors"
                :class="
                  opt.value === selectedInboxId
                    ? 'text-woot-600 dark:text-woot-400 font-semibold bg-woot-50 dark:bg-woot-900/20'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                "
                @click="selectInbox(opt.value)"
              >
                <span>{{ opt.label }}</span>
                <span
                  v-if="opt.value === selectedInboxId"
                  class="i-lucide-check text-woot-500 text-xs shrink-0"
                />
              </button>
            </div>
          </transition>
        </div>

        <div class="flex items-center rounded-full bg-slate-100 dark:bg-slate-800 p-0.5 gap-0.5">
          <!-- Períodos fixos -->
          <button
            v-for="p in PERIOD_OPTIONS.filter(o => o.key !== 'custom')"
            :key="p.key"
            class="px-3 py-1 text-xs font-medium rounded-full transition-all duration-200"
            :class="
              selectedPeriod === p.key
                ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            "
            @click="selectedPeriod = p.key"
          >
            {{ p.label }}
          </button>

          <!-- Personalizado — integrado na pill row -->
          <div ref="customPickerRef" class="relative">
            <button
              class="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full transition-all duration-200"
              :class="
                selectedPeriod === 'custom'
                  ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              "
              @click.stop="handleCustomClick"
            >
              <span>{{ customLabel }}</span>
              <span
                class="i-lucide-chevron-down text-[10px] text-slate-400 transition-transform duration-200"
                :class="{ 'rotate-180': customPickerOpen }"
              />
            </button>

            <!-- Popover de seleção de datas -->
            <transition
              enter-active-class="transition duration-150 ease-out"
              enter-from-class="opacity-0 scale-95 -translate-y-1"
              enter-to-class="opacity-100 scale-100 translate-y-0"
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100 scale-100 translate-y-0"
              leave-to-class="opacity-0 scale-95 -translate-y-1"
            >
              <div
                v-if="customPickerOpen"
                class="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-3 w-56"
              >
                <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2.5">
                  Intervalo personalizado
                </p>
                <div class="flex flex-col gap-2">
                  <div class="flex flex-col gap-1">
                    <label class="text-[10px] text-slate-400 dark:text-slate-500">De</label>
                    <input
                      v-model="customFrom"
                      type="date"
                      class="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-woot-400 dark:focus:ring-woot-500 transition-colors"
                    />
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="text-[10px] text-slate-400 dark:text-slate-500">Até</label>
                    <input
                      v-model="customTo"
                      type="date"
                      class="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-woot-400 dark:focus:ring-woot-500 transition-colors"
                    />
                  </div>
                  <button
                    class="mt-1 w-full py-1.5 text-xs font-semibold rounded-lg bg-woot-500 hover:bg-woot-600 text-white transition-colors disabled:opacity-50"
                    :disabled="!customFrom || !customTo"
                    @click="applyCustomPeriod"
                  >
                    Aplicar
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Abas -->
    <div class="flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 -mb-2">
      <button
        class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
        :class="activeTab === 'overview'
          ? 'border-woot-500 text-woot-600 dark:text-woot-400'
          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'"
        @click="activeTab = 'overview'"
      >
        Visão geral
      </button>
      <button
        class="px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5"
        :class="activeTab === 'audit'
          ? 'border-woot-500 text-woot-600 dark:text-woot-400'
          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'"
        @click="activeTab = 'audit'"
      >
        <span class="i-lucide-shield-check text-xs" />
        Auditoria
      </button>
    </div>

    <!-- Aba: Auditoria -->
    <AuditStats
      v-if="activeTab === 'audit'"
      :since="periodTimes.since"
      :until="periodTimes.until"
    />

    <!-- Loading -->
    <div v-if="activeTab === 'overview' && isLoading" class="flex items-center justify-center py-20">
      <span class="i-lucide-loader-circle animate-spin text-3xl text-woot-500" />
    </div>

    <template v-if="activeTab === 'overview' && !isLoading">
      <!-- Error -->
      <div
        v-if="error"
        class="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400 text-sm"
      >
        <span class="i-lucide-alert-circle shrink-0" />
        <span>Erro ao carregar dados.</span>
        <button class="ml-auto underline" @click="loadData">
          Tentar novamente
        </button>
      </div>

      <!-- KPI Cards -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <!-- Conversas -->
        <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
              <span class="i-lucide-messages-square text-slate-500 dark:text-slate-400 text-sm" />
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400">Conversas</p>
          </div>
          <p class="text-2xl font-bold text-slate-800 dark:text-slate-100">{{ error ? '—' : conversations }}</p>
          <div class="flex items-center gap-1.5 mt-0.5">
            <p class="text-xs text-slate-400">no período</p>
            <span v-if="formatDelta(delta.conversations_pct)" class="text-xs font-medium" :class="deltaClass(delta.conversations_pct)">
              {{ formatDelta(delta.conversations_pct) }}
            </span>
          </div>
        </div>

        <!-- Só IA -->
        <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
              <span class="i-lucide-bot text-emerald-600 dark:text-emerald-400 text-sm" />
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400">Só IA</p>
          </div>
          <p class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{{ error ? '—' : `${aiOnly.percentage}%` }}</p>
          <p class="text-xs text-slate-400 mt-0.5">{{ error ? '' : `${aiOnly.count} conversas` }}</p>
        </div>

        <!-- Transferidas -->
        <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
              <span class="i-lucide-user-round-check text-amber-600 dark:text-amber-400 text-sm" />
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400">Transferidas</p>
          </div>
          <p class="text-2xl font-bold text-amber-600 dark:text-amber-400">{{ error ? '—' : `${transferred.percentage}%` }}</p>
          <div class="flex items-center gap-1.5 mt-0.5">
            <p class="text-xs text-slate-400">{{ error ? '' : `${transferred.count} conversas` }}</p>
            <span v-if="formatDelta(delta.transferred_pct)" class="text-xs font-medium" :class="deltaClass(-delta.transferred_pct)">
              {{ formatDelta(delta.transferred_pct) }}
            </span>
          </div>
        </div>

        <!-- Tempo médio -->
        <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-7 h-7 rounded-lg bg-violet-50 dark:bg-violet-900/20 flex items-center justify-center shrink-0">
              <span class="i-lucide-timer text-violet-600 dark:text-violet-400 text-sm" />
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400">Tempo médio</p>
          </div>
          <p class="text-2xl font-bold text-slate-800 dark:text-slate-100">{{ error ? '—' : avgDurationFormatted }}</p>
          <p class="text-xs text-slate-400 mt-0.5">por conversa</p>
        </div>
      </div>

      <!-- Unit Cards -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div
          v-for="unit in byUnit"
          :key="unit.inbox_id"
          class="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col"
        >
          <!-- Unit header -->
          <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <h2 class="text-sm font-semibold text-slate-800 dark:text-slate-100">{{ unit.name }}</h2>
            <span class="text-xs font-medium text-slate-400 tabular-nums">
              {{ unit.conversations }} conversa{{ unit.conversations !== 1 ? 's' : '' }}
            </span>
          </div>

          <!-- AI vs Transferred bar -->
          <div class="px-4 pt-3 pb-2">
            <div class="flex h-2 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800 mb-2">
              <div class="bg-emerald-500 transition-all duration-500" :style="{ width: `${unit.ai_only_percentage}%` }" />
              <div class="bg-amber-400 transition-all duration-500" :style="{ width: `${unit.transferred_percentage}%` }" />
            </div>
            <div class="flex items-center justify-between text-xs">
              <div class="flex items-center gap-1.5">
                <span class="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                <span class="text-slate-500 dark:text-slate-400">Só IA</span>
                <span class="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{{ unit.ai_only_percentage }}%</span>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="font-semibold text-amber-500 tabular-nums">{{ unit.transferred_percentage }}%</span>
                <span class="text-slate-500 dark:text-slate-400">Transferidas</span>
                <span class="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
              </div>
            </div>
          </div>

          <!-- Motivos -->
          <div class="px-4 pb-4 pt-3 flex-1">
            <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">
              Motivos de transferência
            </p>
            <!-- Tempo até transferência -->
            <div v-if="formatTime(unit.avg_time_to_transfer_seconds)" class="flex items-center gap-1.5 mb-3 text-xs text-slate-400">
              <span class="i-lucide-hourglass text-xs" />
              <span>Tempo médio até transferir:
                <span class="font-semibold text-slate-600 dark:text-slate-300">{{ formatTime(unit.avg_time_to_transfer_seconds) }}</span>
              </span>
            </div>
            <p v-if="unit.top_motivos.length === 0" class="text-sm text-slate-400 py-3 text-center italic">
              Sem transferências no período
            </p>
            <ol v-else class="flex flex-col gap-2.5">
              <li
                v-for="(item, index) in unit.top_motivos"
                :key="item.motivo"
                class="flex items-start gap-2.5 group"
              >
                <span class="w-4 h-4 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {{ index + 1 }}
                </span>
                <div class="flex-1 min-w-0">
                  <button
                    class="text-left text-sm text-slate-700 dark:text-slate-200 break-words leading-snug hover:text-woot-600 dark:hover:text-woot-400 transition-colors w-full"
                    :disabled="!item.conversation_ids?.length"
                    @click="item.conversation_ids?.length && openMotivoModal(item)"
                  >
                    {{ item.motivo }}
                    <span v-if="item.conversation_ids?.length" class="i-lucide-external-link text-xs text-slate-300 dark:text-slate-600 group-hover:text-woot-400 ml-1 align-middle" />
                  </button>
                  <p class="text-xs text-slate-400 mt-0.5 tabular-nums">
                    {{ item.count }} {{ item.count === 1 ? 'ocorrência' : 'ocorrências' }}
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <!-- Top Intents (AI-only) -->
      <div v-if="topIntents.length > 0" class="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
            <span class="i-lucide-sparkles text-emerald-600 dark:text-emerald-400 text-sm" />
          </div>
          <div>
            <h2 class="text-sm font-semibold text-slate-800 dark:text-slate-100">O que a IA resolveu</h2>
            <p class="text-xs text-slate-400 dark:text-slate-500">Top intents das conversas sem transferência</p>
          </div>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          <button
            v-for="(item, index) in topIntents"
            :key="item.intent"
            class="flex flex-col gap-1 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 text-left transition-colors group hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-200 dark:hover:border-emerald-700 cursor-pointer"
            @click="openIntentModal(item)"
          >
            <div class="flex items-center gap-1.5">
              <span class="w-4 h-4 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center justify-center shrink-0">
                {{ index + 1 }}
              </span>
              <span class="text-lg font-bold text-slate-800 dark:text-slate-100 tabular-nums">{{ item.count }}</span>
              <span v-if="item.conversation_ids?.length" class="i-lucide-external-link text-xs text-slate-300 dark:text-slate-600 group-hover:text-emerald-500 transition-colors ml-auto" />
            </div>
            <p class="text-xs text-slate-600 dark:text-slate-300 leading-snug group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition-colors">{{ item.intent }}</p>
          </button>
        </div>
      </div>
    </template>

    <!-- Modal de conversas -->
    <teleport to="body">
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="activeModal"
          class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          @click.self="closeConvModal"
        >
          <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeConvModal" />
          <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden">
            <!-- Modal header -->
            <div class="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
              <div>
                <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Conversas</p>
                <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug">{{ activeModal.title }}</h3>
              </div>
              <button
                class="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                @click="closeConvModal"
              >
                <span class="i-lucide-x text-sm" />
              </button>
            </div>
            <!-- Modal body -->
            <div class="overflow-y-auto flex-1 px-5 py-3">
              <p class="text-xs text-slate-400 mb-3">{{ activeModal.conversation_ids.length }} conversa{{ activeModal.conversation_ids.length !== 1 ? 's' : '' }}</p>
              <div v-if="activeModal.conversation_ids.length === 0" class="flex flex-col items-center gap-2 py-6 text-center">
                <span class="i-lucide-info text-slate-300 dark:text-slate-600 text-2xl" />
                <p class="text-xs text-slate-400 dark:text-slate-500 leading-snug">Lista de conversas ainda não disponível.<br>Será mapeada no próximo ciclo de análise.</p>
              </div>
              <div v-else class="flex flex-col gap-1.5">
                <button
                  v-for="conv in modalConversations"
                  :key="conv.id"
                  class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left bg-slate-50 dark:bg-slate-800/50 hover:bg-woot-50 dark:hover:bg-woot-900/20 border border-slate-100 dark:border-slate-700/50 hover:border-woot-200 dark:hover:border-woot-700 transition-colors group"
                  @click="goToConversation(conv.id)"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center shrink-0">
                      <span class="i-lucide-user text-xs text-slate-500 dark:text-slate-400" />
                    </span>
                    <span v-if="conv.name" class="text-sm text-slate-700 dark:text-slate-200 group-hover:text-woot-700 dark:group-hover:text-woot-300 truncate">
                      {{ conv.name }}
                    </span>
                    <span v-else class="text-sm text-slate-400 dark:text-slate-500 tabular-nums">
                      #{{ conv.id }}
                    </span>
                  </div>
                  <span class="i-lucide-arrow-right text-xs text-slate-300 dark:text-slate-600 group-hover:text-woot-500 transition-colors shrink-0" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>

    </teleport>
  </div>
</template>
